import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
  BankAndTermsFormValues,
  CompanyDataFormValues,
  CompanyRegistrationUpdate,
  Term,
  VerificationFormValues,
} from './schema';

export interface RegistrationFormState {
  companyData: CompanyDataFormValues;
  verification: VerificationFormValues;
  bankAndTerms: BankAndTermsFormValues;
  acceptedTerms: Term[];
}

const emptyAddress = {
  street: '',
  zip_code: '',
  city: '',
  country_code: '',
  country_name: '',
  street2: '',
};

const defaultFormState = (): RegistrationFormState => ({
  companyData: {
    company_name: '',
    company_tax_id: '',
    company_bin: '',
    warehouse: '',
    address: { ...emptyAddress },
    remission_address: {
      company: '',
      address: { ...emptyAddress },
    },
  },
  verification: {
    partner_name: '',
    partner_position: '',
    partner_email: '',
    partner_phone: '',
  },
  bankAndTerms: {
    payment_terms: 'invoice',
    bank_account: {
      bank_name: '',
      iban: '',
      bic: '',
    },
    accepted_term_ids: [],
  },
  acceptedTerms: [],
});

export const registrationToFormState = (registration: CompanyRegistrationUpdate | undefined): RegistrationFormState => {
  const base = defaultFormState();
  if (!registration) return base;

  const address = registration.address ?? {};
  const remission = registration.remission_address ?? {};
  const remissionAddress = remission.address ?? {};
  const acceptedTerms = registration.company_term_ids ?? [];

  return {
    companyData: {
      company_name: registration.company_name ?? '',
      company_tax_id: registration.company_tax_id ?? '',
      company_bin: registration.company_bin ?? '',
      warehouse: registration.warehouse ?? '',
      address: {
        street: address.street ?? '',
        zip_code: address.zip_code ?? '',
        city: address.city ?? '',
        country_code: address.country_code ?? '',
        country_name: address.country_name ?? '',
        street2: address.street2 ?? '',
      },
      remission_address: {
        company: remission.company ?? '',
        address: {
          street: remissionAddress.street ?? '',
          zip_code: remissionAddress.zip_code ?? '',
          city: remissionAddress.city ?? '',
          country_code: remissionAddress.country_code ?? '',
          country_name: remissionAddress.country_name ?? '',
          street2: remissionAddress.street2 ?? '',
        },
      },
    },
    verification: {
      partner_name: registration.partner_name ?? '',
      partner_position: registration.partner_position ?? '',
      partner_email: registration.partner_email ?? '',
      partner_phone: registration.partner_phone ?? '',
    },
    bankAndTerms: {
      payment_terms: registration.payment_terms ?? 'invoice',
      bank_account: {
        bank_name: registration.bank_account?.bank_name ?? '',
        iban: registration.bank_account?.iban ?? '',
        bic: registration.bank_account?.bic ?? '',
      },
      accepted_term_ids: acceptedTerms.map(t => t.id).filter((id): id is number => id != null),
    },
    acceptedTerms,
  };
};

interface RegistrationFormContextType {
  formState: RegistrationFormState;
  setFormState: React.Dispatch<React.SetStateAction<RegistrationFormState>>;
  hydrate: (registration: CompanyRegistrationUpdate | undefined) => void;
  resetFormState: () => void;
}

const RegistrationFormContext = createContext<RegistrationFormContextType | undefined>(undefined);

export const RegistrationFormContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [formState, setFormState] = useState<RegistrationFormState>(() => defaultFormState());

  const hydrate = useCallback(
    (registration: CompanyRegistrationUpdate | undefined) => setFormState(registrationToFormState(registration)),
    [],
  );
  const resetFormState = useCallback(() => setFormState(defaultFormState()), []);

  const value = useMemo<RegistrationFormContextType>(
    () => ({ formState, setFormState, hydrate, resetFormState }),
    [formState, hydrate, resetFormState],
  );

  return <RegistrationFormContext.Provider value={value}>{children}</RegistrationFormContext.Provider>;
};

export const useRegistrationFormContext = (): RegistrationFormContextType => {
  const context = useContext(RegistrationFormContext);
  if (!context) {
    throw new Error('useRegistrationFormContext must be used within a RegistrationFormContextProvider');
  }
  return context;
};
